import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { TooltipProvider } from '@/ui/tooltip';
import { Guard, RedirectIfSignedIn } from './guard';
import { VendorShell } from '@/shells/vendor-shell';
import { InternalShell } from '@/shells/internal-shell';
import { Stub } from '@/screens/stub';
import { MfaBackupCodes, MfaChallenge, MfaReEnrol, MfaRecovery, MfaSetup } from '@/screens/shared/mfa';

import { VendorLogin } from '@/screens/vendor/login';
import { VendorJoin } from '@/screens/vendor/join';
import { VendorOnboarding } from '@/screens/vendor/onboarding';
import { VendorHome } from '@/screens/vendor/home';
import { VendorSubmissions } from '@/screens/vendor/submissions';
import { VendorSubmissionDetail } from '@/screens/vendor/submission-detail';
import { VendorProfile } from '@/screens/vendor/profile';

import { InternalLogin } from '@/screens/internal/login';
import { InternalDashboard } from '@/screens/internal/dashboard';
import { InternalVendors } from '@/screens/internal/vendors';
import { DatasetsCatalog } from '@/screens/internal/datasets-catalog';
import { DatasetsRemediation } from '@/screens/internal/datasets-layout';
import { DatasetsSubmissions } from '@/screens/internal/datasets-submissions';
import { DatasetDetailScreen } from '@/screens/internal/dataset-detail';

/**
 * Two route trees, separated by path prefix: /v for the vendor portal, /admin for the operator
 * console. Subdomains are the production shape described in frontend.md section 1, but wildcard
 * tenant subdomains are not available on *.pages.dev, and the API guard already accepts the
 * tenant as a header (apps/api/src/auth.ts:50). Only tenant resolution changes if we move.
 *
 * Routes marked Stub are in navigation but have no mockup - see FRONTEND_REBUILD.md section 6.
 */
export function Router() {
  return (
    <BrowserRouter>
      <TooltipProvider delayDuration={200}>
        <Routes>
          <Route path="/" element={<Navigate to="/v" replace />} />

          {/* ---------------------------------------------------- vendor portal */}
          <Route
            path="/v/login"
            element={
              <RedirectIfSignedIn realm="vendor" to="/v">
                <VendorLogin />
              </RedirectIfSignedIn>
            }
          />
          {/* Where an invite link lands. Public by necessity: the account it registers has no
              password yet, so there is nothing to authenticate with until this screen is done. */}
          <Route path="/v/join/:token" element={<VendorJoin />} />
          {/* Reachable without a session by design: the person asking has lost their factor. */}
          <Route path="/v/mfa/recover" element={<MfaRecovery />} />
          <Route element={<Guard realm="vendor" loginPath="/v/login" />}>
            {/* Guard destinations. Inside the guard, so that resolving one moves the user on --
                each screen invalidates `/me` and the guard re-resolves -- but outside the shell,
                because the user is not cleared to see the portal chrome while one is pending. */}
            <Route path="/v/mfa/setup" element={<MfaSetup realm="vendor" />} />
            <Route path="/v/mfa/challenge" element={<MfaChallenge realm="vendor" />} />
            <Route path="/v/mfa/backup-codes" element={<MfaBackupCodes realm="vendor" />} />
            <Route path="/v/mfa/reenroll" element={<MfaReEnrol realm="vendor" />} />
            <Route path="/v/onboarding" element={<VendorOnboarding />} />
            <Route path="/v" element={<VendorShell />}>
              <Route index element={<VendorHome />} />
              <Route path="submissions" element={<VendorSubmissions />} />
              <Route path="submissions/:id" element={<VendorSubmissionDetail />} />
              <Route path="profile" element={<VendorProfile />} />
              <Route path="engagements" element={<Stub title="Engagements" />} />
              <Route path="remediation" element={<Stub title="Remediation" />} />
            </Route>
          </Route>

          {/* -------------------------------------------------- operator console */}
          <Route
            path="/admin/login"
            element={
              <RedirectIfSignedIn realm="admin" to="/admin">
                <InternalLogin />
              </RedirectIfSignedIn>
            }
          />
          <Route element={<Guard realm="admin" loginPath="/admin/login" />}>
            <Route path="/admin/mfa/setup" element={<MfaSetup realm="admin" />} />
            <Route path="/admin/mfa/challenge" element={<MfaChallenge realm="admin" />} />
            <Route path="/admin/mfa/backup-codes" element={<MfaBackupCodes realm="admin" />} />
            <Route path="/admin/mfa/reenroll" element={<MfaReEnrol realm="admin" />} />
            <Route path="/admin" element={<InternalShell />}>
              <Route index element={<InternalDashboard />} />

              <Route path="vendors" element={<InternalVendors />} />
              <Route path="vendors/drawer" element={<Stub title="Vendor detail drawer" />} />
              <Route path="vendors/page" element={<Stub title="Vendor detail page" />} />

              {/* Submissions is the landing tab: it is the operator's working list. Catalog is
                  finished products. Detail is a drill-down, not a tab -- it needs a row to be about. */}
              <Route path="datasets" element={<DatasetsSubmissions />} />
              <Route path="datasets/catalog" element={<DatasetsCatalog />} />
              <Route path="datasets/remediation" element={<DatasetsRemediation />} />
              <Route path="datasets/:id" element={<DatasetDetailScreen />} />

              <Route path="engagements" element={<Stub title="Engagements" />} />
              <Route path="data-factory" element={<Stub title="Data Factory" />} />
              <Route path="delivery" element={<Stub title="Delivery" />} />
              <Route path="settings" element={<Stub title="Settings" />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/v" replace />} />
        </Routes>
      </TooltipProvider>
    </BrowserRouter>
  );
}
